<script setup lang="ts">
import { navigateTo } from 'nuxt/app'
import { nextTick, onBeforeUnmount, watch } from 'vue'
import { getFooterHotCountries, type FooterHotCountryItem } from '~/api/footer'
import { request } from '~/api/request'
import { useAppObjectCookie } from '~/composables/useAppCookie'
import { useEnvApi } from '~/composables/useEnvApi'
import { useAppStore } from '~/stores/app'
import RiskDialogClose from '~/assets/icon/close-2.svg'
import CountryArrow from '~/assets/images/footer/country-arrow.svg'
import CountryClear from '~/assets/images/footer/country-clear.svg'
import DividerLine from '~/assets/images/footer/divider-line.svg'
import FooterSocialFacebook from '~/assets/images/footer/social-facebook.svg'
import FooterSocialLinkedin from '~/assets/images/footer/social-linkedin.svg'
import FooterSocialTelegram from '~/assets/images/footer/social-telegram.svg'
import FooterSocialX from '~/assets/images/footer/social-x.svg'
import FooterSocialXMark from '~/assets/images/footer/social-x-mark.svg'
import WikiBitFooterLogo from '~/assets/images/footer/wikibit-footer-logo.svg'

import ColumnDivider from '~/assets/images/footer/country-panel/column-divider.svg'
import IconCheck from '~/assets/images/footer/country-panel/icon-check.svg'
import IconClose from '~/assets/images/footer/country-panel/icon-close.svg'
import IconMap from '~/assets/images/footer/country-panel/icon-map.svg'
import IconSearch from '~/assets/images/footer/country-panel/icon-search.svg'
import areaCodeCN from '~/assets/json/areaCodeCN.json'
import areaCodeEn from '~/assets/json/areaCodeEn.json'

type CountryItem = {
  abbr?: string
  code?: string
  countryCode?: string
  name: string
  url: string
  flag: string
  twoCharCode?: string
}

type SearchResultItem = Partial<CountryItem> & {
  name: string
  flag: string
  'flag-c'?: string
  countryCode?: string
}

type CountryGroup = {
  name: string
  data: FooterHotCountryItem[]
}

type SelectedCountryDisplay = {
  code: string
  country: string
  flag: string
}

const { locale, localePath, t } = useAppI18n()

type FooterNavItem = {
  label: string
  href: string
  routeName?: string
  path?: string
  termsType?: '1' | '3'
  riskWarning?: boolean
  openInNewTab?: boolean
}

const DEFAULT_COUNTRY_CODE = '840'
const DEFAULT_COUNTRY_TWO_CHAR_CODE = 'us'
const DEFAULT_COUNTRY_NAME = 'United States'

// 页脚顶部导航：当前为占位链接，后续可替换真实跳转地址。
const navItems: FooterNavItem[] = [
  { label: t('82486'), href: '#', routeName: 'about', openInNewTab: true },
  { label: t('82489'), href: '#', routeName: 'contact', openInNewTab: true },
  { label: t('82525'), href: '#', riskWarning: true },
  { label: t('82526'), href: '#', termsType: '1', openInNewTab: true },
  { label: t('82527'), href: '#', termsType: '3', openInNewTab: true },
  { label: t('83111'), href: 'https://v.wikibit.com/zh-cn', openInNewTab: true },
]

const introText = computed(() => t('82537'))
const riskWarningParagraphs = [t('84417'), t('84418'), t('84419')]

// 联系方式文案：按行渲染，内容由语言包提供，便于多语言统一维护。
const contactLines = computed(() => [
  `${t('82529')}contact@wikibit.com；`,
  `${t('82531')}(SG)+0065 31388986；`,
  `${t('82532')}qa@wikibit.com；`,
  `${t('82533')}contact@wikibit.com`,
])

const appStore = useAppStore()
const route = useRoute()
const api = useEnvApi()
const matomo = useMatomo()

const countryList = ref<CountryItem[]>([])
const searchcounryList = ref<SearchResultItem[]>([])
const mySearch = ref('')
// 风险提示弹框展示状态：由页脚“风险提示”导航触发。
const isRiskWarningDialogVisible = ref(false)
// 持久化记录用户最近一次选中的展示信息，保证刷新后仍按所选国家/地区文案显示。
const defaultSelectedCountryDisplay: SelectedCountryDisplay = { code: '', country: '', flag: '' }
const selectedCountryDisplayCookie = useAppObjectCookie<SelectedCountryDisplay>(
  'footer_selected_country_display',
  defaultSelectedCountryDisplay,
)
const selectedCountryDisplay = ref(selectedCountryDisplayCookie.value || defaultSelectedCountryDisplay)

// 国家面板是否可见。
const isCountryPanelVisible = ref(false)
// 输入框聚焦状态：用于控制建议浮层的表现。
const isSearchFocused = ref(false)
// 国家面板节点引用：用于鼠标命中判断。
const countryPanelRef = ref<HTMLElement | null>(null)
// 页脚触发器节点引用：用于与面板联动判断。
const countrySelectWrapRef = ref<HTMLElement | null>(null)
// 搜索建议浮层节点引用：用于每次搜索后重置滚动位置。
const searchPopoverRef = ref<HTMLElement | null>(null)
// 延时关闭计时器：用于 hover 切换时防闪烁。
let closePanelTimer: ReturnType<typeof setTimeout> | null = null
// 搜索请求序号：避免连续输入时旧接口响应覆盖最新结果。
let searchRequestSequence = 0

const splitCountryColumns = (list: CountryItem[], columnCount = 6) => {
  if (list.length <= 0) {
    return []
  }

  const columns = Array.from({ length: columnCount }, () => [] as CountryItem[])
  const chunk = Math.ceil(list.length / columnCount)
  for (let index = 0; index < list.length; index += 1) {
    const columnIndex = Math.floor(index / chunk)
    if (!columns[columnIndex]) {
      continue
    }
    columns[columnIndex].push(list[index]!)
  }

  return columns.filter((column) => column.length > 0)
}

const getCountryAbbr = (item?: Partial<CountryItem> | null) => {
  return (item?.abbr || item?.twoCharCode || '').toLowerCase()
}

const getCountryTwoCharCode = (item?: Partial<CountryItem> | null) => {
  return (item?.twoCharCode || item?.abbr || '').toLowerCase()
}

const getCountryNumericCode = (item?: Partial<CountryItem> | null) => {
  const countryCode = (item?.countryCode || '').trim()
  if (countryCode) {
    return countryCode
  }

  return countryCodeByTwoCharCode.get(getCountryTwoCharCode(item)) || ''
}

const flattenCountryGroups = (groups: CountryGroup[]) => {
  return groups.flatMap((group) => group.data || [])
}

const countryCodeByTwoCharCode = new Map(
  [...flattenCountryGroups(areaCodeCN as CountryGroup[]), ...flattenCountryGroups(areaCodeEn as CountryGroup[])]
    .map((item) => [getCountryTwoCharCode(item), item.countryCode || ''] as const)
    .filter(([twoCharCode, countryCode]) => twoCharCode && countryCode),
)

const selectedCountryCode = computed(() => (appStore.CountryCode || '').toLowerCase())
// 页面首次没有 CountryCode 时，底部选择器默认展示美国，列表选中态保持同一个兜底国家。
const effectiveSelectedCountryCode = computed(() => selectedCountryCode.value || DEFAULT_COUNTRY_CODE)
// 中国大陆访问时，面板头部按业务要求展示中国香港及其当前语言文案。
const currentCountryPanelCode = computed(() => effectiveSelectedCountryCode.value)
const selectedLanguageCode = computed(() => (appStore.LanguageCode || locale).toLowerCase())
const isChineseLanguage = computed(() => selectedLanguageCode.value.startsWith('zh'))

const normalizeCountryItem = (item: FooterHotCountryItem): CountryItem => {
  return {
    abbr: item.abbr,
    code: item.code,
    countryCode: item.countryCode,
    name: item.name || '',
    url: item.url || '',
    flag: item['flag-c'] || item.flag || '',
    twoCharCode: item.twoCharCode,
  }
}

const normalizeSearchResultItem = (item: SearchResultItem): SearchResultItem => {
  return {
    ...item,
    name: item.name || '',
    flag: item['flag-c'] || item.flag || '',
  }
}

const isValidSearchResultItem = (item: SearchResultItem) => {
  return Boolean(item.name.trim() && item.flag.trim() && getCountryNumericCode(item))
}

const countryDisplayListCN = flattenCountryGroups(areaCodeCN as CountryGroup[]).map(normalizeCountryItem)
const countryDisplayListEN = flattenCountryGroups(areaCodeEn as CountryGroup[]).map(normalizeCountryItem)

const findDisplayCountryItem = (countryCode: string) => {
  const normalizedCode = countryCode.trim().toLowerCase()
  if (!normalizedCode) {
    return null
  }

  const preferredList = isChineseLanguage.value ? countryDisplayListCN : countryDisplayListEN
  return (
    preferredList.find((item) => getCountryNumericCode(item) === normalizedCode) ||
    preferredList.find((item) => getCountryTwoCharCode(item) === normalizedCode) ||
    countryDisplayListEN.find((item) => getCountryNumericCode(item) === normalizedCode) ||
    countryDisplayListEN.find((item) => getCountryTwoCharCode(item) === normalizedCode) ||
    null
  )
}

const findCountryListItem = (countryCode: string) => {
  const normalizedCode = countryCode.trim().toLowerCase()
  if (!normalizedCode) {
    return null
  }

  return (
    countryList.value.find((item) => getCountryNumericCode(item) === normalizedCode) ||
    countryList.value.find((item) => getCountryTwoCharCode(item) === normalizedCode) ||
    countryList.value.find((item) => getCountryAbbr(item) === normalizedCode) ||
    null
  )
}

const currentCountry = computed<CountryItem>(() => {
  const displayCountryCode = effectiveSelectedCountryCode.value
  const matchedCountry = findCountryListItem(displayCountryCode)
  const fallbackDisplayCountry = findDisplayCountryItem(displayCountryCode)
  const displayTwoCharCode = getCountryTwoCharCode(matchedCountry) || getCountryTwoCharCode(fallbackDisplayCountry)
  const selectedSearchCountry =
    selectedCountryDisplay.value.code === selectedCountryCode.value && selectedCountryDisplay.value.country
      ? {
          ...fallbackDisplayCountry,
          name: selectedCountryDisplay.value.country,
          flag: fallbackDisplayCountry?.flag || matchedCountry?.flag || selectedCountryDisplay.value.flag || '',
          abbr: displayTwoCharCode,
          countryCode: displayCountryCode,
          twoCharCode: displayTwoCharCode,
        }
      : null

  if (selectedSearchCountry) {
    return selectedSearchCountry
  }

  if (matchedCountry) {
    return matchedCountry
  }

  if (fallbackDisplayCountry) {
    return fallbackDisplayCountry
  }

  return {
    name: selectedCountryDisplay.value.country || DEFAULT_COUNTRY_NAME,
    url: 'www.wikibit.cn',
    flag: selectedCountryDisplay.value.flag || '',
    abbr: displayTwoCharCode || DEFAULT_COUNTRY_TWO_CHAR_CODE,
    countryCode: displayCountryCode,
    twoCharCode: displayTwoCharCode || DEFAULT_COUNTRY_TWO_CHAR_CODE,
  }
})

const currentCountryName = computed(() => {
  return currentCountry.value.name || selectedCountryDisplay.value.country || DEFAULT_COUNTRY_NAME
})

const currentCountryPanelDisplay = computed(() => findDisplayCountryItem(effectiveSelectedCountryCode.value))

// 面板头部国家名按当前选中地区代码取值，保持与国旗映射解耦。
const currentCountryPanelName = computed(() => {
  return currentCountryPanelDisplay.value?.name || currentCountryName.value
})

// 默认国家不显示清除按钮，避免用户在初始状态看到无效操作入口。
const showCountryClearButton = computed(() => effectiveSelectedCountryCode.value !== DEFAULT_COUNTRY_CODE)

const currentCountryDisplayFlag = computed(() => currentCountry.value.flag)

const currentCountryPanelFlag = computed(
  () => currentCountryPanelDisplay.value?.flag || currentCountryDisplayFlag.value,
)
const currentCountryFooterFlag = computed(() => currentCountryPanelFlag.value)

const { data: hotCountryList } = await useAsyncData(
  'footer-hot-countries',
  async () => {
    const response = await getFooterHotCountries()
    const payload = Array.isArray(response) ? response : response?.data

    return Array.isArray(payload) ? payload.map((item) => normalizeCountryItem(item)) : []
  },
  {
    default: () => [],
  },
)

watchEffect(() => {
  countryList.value = hotCountryList.value || []
})

const countryColumns = computed(() => splitCountryColumns(countryList.value, 6))
const showSearchPopover = computed(() => isCountryPanelVisible.value && searchcounryList.value.length > 0)
const isSearchInvalid = computed(() => mySearch.value.trim().length > 0 && searchcounryList.value.length <= 0)

const getSearchMatchedNameParts = (name: string) => {
  const keyword = mySearch.value.trim()
  if (!keyword) {
    return [{ text: name, matched: false }]
  }

  const nameLower = name.toLowerCase()
  const keywordLower = keyword.toLowerCase()
  const startIndex = nameLower.indexOf(keywordLower)
  if (startIndex < 0) {
    return [{ text: name, matched: false }]
  }

  const endIndex = startIndex + keyword.length
  return [
    { text: name.slice(0, startIndex), matched: false },
    { text: name.slice(startIndex, endIndex), matched: true },
    { text: name.slice(endIndex), matched: false },
  ].filter((part) => part.text)
}

// 搜索结果更新后回到顶部，避免沿用上一次结果列表的滚动位置。
const resetSearchPopoverScroll = async () => {
  await nextTick()
  if (!searchPopoverRef.value) {
    return
  }

  searchPopoverRef.value.scrollTop = 0
}

// 统一清理延时器，避免重复定时和卸载后残留回调。
const cancelCloseCountryPanel = () => {
  if (!closePanelTimer) {
    return
  }

  clearTimeout(closePanelTimer)
  closePanelTimer = null
}

// 打开面板前先取消关闭调度，避免触发器和面板切换时误关。
const openCountryPanel = () => {
  cancelCloseCountryPanel()
  isCountryPanelVisible.value = true
}

// 关闭面板并重置搜索相关状态。
const closeCountryPanel = () => {
  cancelCloseCountryPanel()
  isCountryPanelVisible.value = false
  isSearchFocused.value = false
  mySearch.value = ''
  searchcounryList.value = []
}

// 延迟关闭：给鼠标从触发器移动到面板留短暂缓冲。
const scheduleCloseCountryPanel = () => {
  if (isSearchFocused.value) {
    return
  }

  cancelCloseCountryPanel()
  closePanelTimer = setTimeout(() => {
    closeCountryPanel()
  }, 120)
}

const getCurrentLocale = () => String(appStore.LanguageCode || locale || 'en')

// 国家切换时复用当前页面路径，但默认语言 en 使用 prefix_except_default 的无前缀路由。
const getCountrySwitchPath = (targetLanguage: string) => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  const firstSegment = pathSegments[0]?.toLowerCase() || ''
  const normalizedSegments = appStore.langList.includes(firstSegment) ? pathSegments.slice(1) : pathSegments
  const normalizedPath = normalizedSegments.join('/')

  return targetLanguage === 'en' ? `/${normalizedPath}` : `/${targetLanguage}/${normalizedPath}`
}

// 协议类导航统一走 Nuxt i18n 路由，并使用路径段区分服务/隐私政策。
const getTermsPath = (type: '1' | '3') => {
  return localePath(`/terms/${type === '1' ? 'service' : 'privacy'}`)
}

const isExternalLink = (href: string) => /^https?:\/\//.test(href)

const handleNavClick = async (item: FooterNavItem) => {
  if (item.riskWarning) {
    openRiskWarningDialog()
    return
  }

  if (!item.path) {
    return
  }

  const targetPath = `/${locale}${item.path}`

  if (item.openInNewTab && import.meta.client) {
    window.open(targetPath, '_blank', 'noopener,noreferrer')
    return
  }

  await navigateTo(targetPath)
}

// 记录页脚导航入口的埋点，并在跳转前完成上报以避免新标签页中断请求。
const trackFooterNavigation = (item: FooterNavItem) => {
  if (item.routeName === 'about') {
    matomo.trackEvent('home_about_us')
    return
  }

  if (item.routeName === 'contact') {
    matomo.trackEvent('contact_us_page', {
      af_source: '底部',
    })
  }
}

// 输入框焦点状态维护。
const focusSearch = () => {
  isSearchFocused.value = true
}

// 输入框失焦时更新状态，不主动清空关键字，保留用户输入。
const blurSearch = () => {
  isSearchFocused.value = false
}

// 打开风险提示弹框。
const openRiskWarningDialog = () => {
  isRiskWarningDialogVisible.value = true
}

// 关闭风险提示弹框。
const closeRiskWarningDialog = () => {
  isRiskWarningDialogVisible.value = false
}

// ESC 关闭弹框，保持与常见对话框交互一致。
const handleRiskWarningKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') {
    return
  }

  closeRiskWarningDialog()
}

// 清空关键字：用于右侧清除按钮点击事件。
const clearSearch = () => {
  mySearch.value = ''
  searchcounryList.value = []
}

const searchCountry = async () => {
  const currentSearchRequestSequence = (searchRequestSequence += 1)
  await resetSearchPopoverScroll()

  if (!mySearch.value.trim()) {
    searchcounryList.value = []
    return
  }

  try {
    const result = await request<SearchResultItem[]>({
      baseURL: api.homeClient,
      url: `${WIKIBITWEBV1}/country/search-v1`,
      method: 'GET',
      params: { content: mySearch.value },
    })
    if (currentSearchRequestSequence !== searchRequestSequence) {
      return
    }
    searchcounryList.value = Array.isArray(result)
      ? result.map((item) => normalizeSearchResultItem(item)).filter(isValidSearchResultItem)
      : []
    await resetSearchPopoverScroll()
  } catch {
    if (currentSearchRequestSequence !== searchRequestSequence) {
      return
    }
    searchcounryList.value = []
  }
}

const normalizeCountryCode = (item: Partial<CountryItem>) => {
  const rawCode = getCountryNumericCode(item)
  if (rawCode) {
    return rawCode
  }

  const matchedCountry = countryList.value.find((country) => {
    return item.code && String(country.code) === String(item.code)
  })

  if (matchedCountry) {
    return getCountryNumericCode(matchedCountry)
  }

  return ''
}

const countryFn = (obj: Partial<CountryItem> = {}, type = 1) => {
  if (type === 1 && isSearchInvalid.value) {
    return
  }

  const countryCode = type === 1 ? getCountryNumericCode(obj) : normalizeCountryCode(obj)
  if (!countryCode) {
    return
  }

  matomo.trackEvent('home_region', {
    af_content: obj.name || '',
  })

  appStore.setCountryCode({ country: countryCode })
  const normalizedCountryCode = appStore.CountryCode
  const nextSelectedCountryDisplay = {
    code: normalizedCountryCode,
    country: obj.name || '',
    flag: obj.flag || '',
  }
  selectedCountryDisplay.value = nextSelectedCountryDisplay
  selectedCountryDisplayCookie.value = nextSelectedCountryDisplay

  const countryLangMap = [
    { lang: 'ja', country: '392' },
    { lang: 'vi', country: '704' },
    { lang: 'th', country: '764' },
    { lang: 'id', country: '360' },
    { lang: 'zh', country: '344' },
    { lang: 'zh', country: '158' },
    { lang: 'es', country: '724' },
    { lang: 'en', country: '036' },
    { lang: 'de', country: '276' },
    { lang: 'en', country: '826' },
    { lang: 'en', country: '840' },
    { lang: 'zh-cn', country: '156' },
    { lang: 'ar', country: '784' },
    { lang: 'ur', country: '586' },
    { lang: 'ru', country: '643' },
    { lang: 'fil', country: '608' },
    { lang: 'hi', country: '356' },
    { lang: 'fr', country: '250' },
    { lang: 'tr', country: '792' },
    { lang: 'pt', country: '620' },
    { lang: 'ko', country: '410' },
  ]

  const targetLanguage = countryLangMap.find((item) => item.country === normalizedCountryCode)?.lang
  if (!import.meta.client) {
    return
  }

  const search = window.location.search ? `${window.location.search}` : ''
  if (targetLanguage) {
    const targetPath = getCountrySwitchPath(targetLanguage)
    window.location.href = search.includes('country=') ? targetPath : `${targetPath}${search}`
    return
  }
  if (window.location.search.includes('country=')) {
    window.location.href = route.path
    return
  }
  window.setTimeout(() => {
    window.location.reload()
  }, 0)
}

// 底部清除按钮只负责恢复默认国家，不参与国家切换的跳转和语言映射逻辑。
const resetDefaultCountry = () => {
  const defaultCountry = findCountryListItem(DEFAULT_COUNTRY_CODE) || findDisplayCountryItem(DEFAULT_COUNTRY_CODE)

  appStore.setCountryCode({ country: DEFAULT_COUNTRY_CODE })
  const nextSelectedCountryDisplay = {
    code: appStore.CountryCode,
    country: defaultCountry?.name || DEFAULT_COUNTRY_NAME,
    flag: defaultCountry?.flag || '',
  }
  selectedCountryDisplay.value = nextSelectedCountryDisplay
  selectedCountryDisplayCookie.value = nextSelectedCountryDisplay

  if (!import.meta.client) {
    return
  }

  // 还原默认国家后刷新页面，确保依赖 CountryCode 的 SSR 和客户端数据都重新拉取。
  window.location.reload()
}

const isSelectedCountry = (country: CountryItem) => getCountryNumericCode(country) === currentCountryPanelCode.value

// 判定离开后目标是否仍处于“触发器或面板”范围内。
const isHoveringTriggerOrPanel = (relatedTarget: EventTarget | null) => {
  if (!(relatedTarget instanceof Node)) {
    return false
  }

  return (
    countryPanelRef.value?.contains(relatedTarget) === true ||
    countrySelectWrapRef.value?.contains(relatedTarget) === true
  )
}

// 鼠标离开触发器：仅在真正离开两块交互区域时才关闭。
const onCountrySelectWrapLeave = (event: MouseEvent) => {
  if (isHoveringTriggerOrPanel(event.relatedTarget)) {
    return
  }

  scheduleCloseCountryPanel()
}

// 鼠标离开面板：与触发器保持一致的关闭策略。
const onCountryPanelLeave = (event: MouseEvent) => {
  if (isHoveringTriggerOrPanel(event.relatedTarget)) {
    return
  }

  scheduleCloseCountryPanel()
}

// 组件卸载时清理计时器，避免潜在内存泄漏。
onBeforeUnmount(() => {
  cancelCloseCountryPanel()
  if (import.meta.client) {
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
    document.documentElement.classList.remove('risk-warning-dialog-open')
    window.removeEventListener('keydown', handleRiskWarningKeydown)
  }
})

watch(isRiskWarningDialogVisible, (visible) => {
  if (!import.meta.client) {
    return
  }

  document.body.style.overflow = visible ? 'hidden' : ''
  // 同时锁定根滚动容器，确保弹框以视口为基准时页面不会继续滚动。
  document.documentElement.style.overflow = visible ? 'hidden' : ''
  document.documentElement.classList.toggle('risk-warning-dialog-open', visible)
  if (visible) {
    window.addEventListener('keydown', handleRiskWarningKeydown)
    return
  }

  window.removeEventListener('keydown', handleRiskWarningKeydown)
})
</script>

<template>
  <!-- 页脚主体容器 -->
  <footer class="wiki-bit-pc-bootom-map" aria-label="WikiBit footer">
    <!-- 国家选择面板：通过 transition 控制进出场动画 -->
    <transition name="country-panel-drawer">
      <section
        v-show="isCountryPanelVisible"
        ref="countryPanelRef"
        class="country-panel"
        @mouseenter="openCountryPanel"
        @mouseleave="onCountryPanelLeave"
      >
        <!-- 面板主容器：头部 + 国家列表 -->
        <div class="country-panel__container">
          <!-- 面板头部：当前地区信息 + 搜索框 -->
          <div class="country-panel-header-row">
            <div class="country-panel-current-country">
              <span class="country-panel-map-icon" aria-hidden="true">
                <IconMap />
              </span>
              <CommonImage
                :src="currentCountryPanelFlag"
                :alt="currentCountryPanelName"
                width="24"
                height="24"
                loading="lazy"
              />
              <span class="country-panel-current-name">{{ currentCountryPanelName }}</span>
            </div>

            <div class="country-panel-search-box">
              <div class="country-panel-search-input-wrap">
                <span class="country-panel-search-icon" aria-hidden="true"><IconSearch /></span>
                <input
                  v-model="mySearch"
                  type="text"
                  class="country-panel-search-input"
                  :placeholder="t('82391')"
                  :aria-label="t('82391')"
                  @focus="focusSearch"
                  @blur="blurSearch"
                  @input="searchCountry"
                />
              </div>
              <span class="country-panel-search-icons">
                <button
                  v-show="mySearch.length > 0"
                  type="button"
                  class="country-panel-close-icon"
                  aria-label="Clear search"
                  @mousedown.prevent
                  @click="clearSearch"
                >
                  <IconClose />
                </button>
              </span>

              <!-- 搜索建议浮层：输入关键字时展示 -->
              <div
                v-show="showSearchPopover"
                ref="searchPopoverRef"
                class="country-panel-search-popover"
                :data-focused="isSearchFocused"
              >
                <button
                  v-for="item in searchcounryList"
                  :key="`${item.code || item.abbr || item.name}`"
                  type="button"
                  class="country-panel-search-result-item"
                  @click="countryFn(item, 2)"
                >
                  <CommonImage
                    :src="item.flag"
                    :alt="item.name"
                    loading="lazy"
                    class="country-panel-search-result-flag"
                    object-fit="cover"
                  />
                  <span class="country-panel-search-result-name">
                    <span
                      v-for="part in getSearchMatchedNameParts(item.name)"
                      :key="`${item.name}-${part.text}-${part.matched}`"
                      :class="{ 'country-panel-search-result-name--matched': part.matched }"
                    >
                      {{ part.text }}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- 国家列表：按列渲染，列间显示分隔符 -->
          <div class="country-panel-list-row">
            <template v-for="(column, columnIndex) in countryColumns" :key="`country-col-${columnIndex}`">
              <div class="country-panel-column">
                <button
                  v-for="country in column"
                  :key="`${columnIndex}-${country.abbr || country.name}`"
                  type="button"
                  :class="['country-panel-item', { 'country-panel-item--selected': isSelectedCountry(country) }]"
                  @click="countryFn(country)"
                >
                  <CommonImage
                    :src="country.flag"
                    :alt="country.name"
                    width="24"
                    height="24"
                    loading="lazy"
                    class="country-panel-item-flag"
                  />

                  <span class="country-panel-item-text">
                    <span
                      :class="[
                        'country-panel-item-name',
                        { 'country-panel-item-name--selected': isSelectedCountry(country) },
                      ]"
                    >
                      {{ country.name }}
                    </span>
                    <span class="country-panel-item-url">{{ country.url }}</span>
                  </span>

                  <!-- <span v-if="isSelectedCountry(country)" class="country-panel-item-check" aria-hidden="true">
                    <IconCheck />
                  </span> -->
                </button>
              </div>

              <div
                v-if="columnIndex < countryColumns.length - 1"
                class="country-panel-column-divider"
                aria-hidden="true"
              >
                <ColumnDivider />
              </div>
            </template>
          </div>
        </div>
      </section>
    </transition>

    <!-- 页脚正文：品牌、地区选择、导航、说明 -->
    <div class="wiki-bit-pc-bootom-map__container">
      <!-- 顶部信息行：品牌信息 + 地区选择入口 -->
      <div class="footer-label-row">
        <div class="footer-brand">
          <WikiBitFooterLogo class="footer-brand-logo" aria-hidden="true" />
          <p class="footer-brand-subtitle">{{ t('82528') }}</p>
        </div>

        <div class="footer-country">
          <p class="footer-country-label">{{ t('82391') }}</p>

          <div
            ref="countrySelectWrapRef"
            class="footer-country-select-wrap"
            @mouseenter="openCountryPanel"
            @mouseleave="onCountrySelectWrapLeave"
          >
            <button
              type="button"
              class="footer-country-select"
              :aria-label="`Selected region: ${currentCountryPanelName}`"
            >
              <span class="footer-country-prefix">
                <CommonImage
                  class="footer-country-flag"
                  :src="currentCountryFooterFlag"
                  :alt="currentCountryPanelName"
                  loading="lazy"
                  width="18"
                  height="18"
                />
                <span class="footer-country-name">{{ currentCountryPanelName }}</span>
              </span>

              <span :class="['footer-country-arrow', { 'footer-country-arrow--expanded': isCountryPanelVisible }]">
                <CountryArrow aria-hidden="true" />
              </span>
            </button>

            <button
              v-if="showCountryClearButton"
              type="button"
              class="footer-country-clear cursor-pointer hover:opacity-80 rtl:!right-auto rtl:!-left-[6px]"
              aria-label="重置国家"
              @click="resetDefaultCountry"
            >
              <CountryClear aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <!-- 分割线 -->
      <div class="footer-divider" aria-hidden="true">
        <DividerLine class="footer-divider-line" />
      </div>

      <!-- Figma node 13102:50705：位于分割线和页脚导航之间，未提供跳转地址。 -->
      <div class="footer-socials" aria-label="WikiBit social media">
        <a
          class="footer-social-link footer-social-icon footer-social-icon--facebook cursor-pointer hover:opacity-80"
          href="https://www.facebook.com/WikiBitAppOfficial/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WikiBit on Facebook"
        >
          <FooterSocialFacebook aria-hidden="true" />
        </a>
        <a
          class="footer-social-link footer-social-x footer-social-icon cursor-pointer hover:opacity-80"
          href="https://x.com/WikiBitOfficial"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WikiBit on X"
        >
          <FooterSocialX class="footer-social-x-background" aria-hidden="true" />
          <FooterSocialXMark class="footer-social-x-mark" aria-hidden="true" />
        </a>
        <a
          class="footer-social-link footer-social-icon footer-social-icon--telegram cursor-pointer hover:opacity-80"
          href="https://t.me/wikibitofficialgroup"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WikiBit on Telegram"
        >
          <FooterSocialTelegram aria-hidden="true" />
        </a>
        <a
          class="footer-social-link footer-social-icon footer-social-icon--linkedin cursor-pointer hover:opacity-80"
          href="https://www.linkedin.com/company/wikibit/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WikiBit on LinkedIn"
        >
          <FooterSocialLinkedin aria-hidden="true" />
        </a>
      </div>

      <!-- 页脚导航 -->
      <nav class="footer-nav" aria-label="Footer navigation">
        <template v-for="(item, index) in navItems" :key="item.label">
          <button
            v-if="item.riskWarning"
            type="button"
            class="footer-nav-link footer-nav-button"
            @click="openRiskWarningDialog"
          >
            {{ item.label }}
          </button>
          <NuxtLink
            v-else-if="item.termsType"
            class="footer-nav-link"
            :to="getTermsPath(item.termsType)"
            :target="item.openInNewTab ? '_blank' : undefined"
            :rel="item.openInNewTab ? 'noopener noreferrer' : undefined"
          >
            {{ item.label }}
          </NuxtLink>
          <NuxtLink
            v-else-if="item.routeName"
            class="footer-nav-link"
            :to="localePath(item.routeName)"
            :target="item.openInNewTab ? '_blank' : undefined"
            :rel="item.openInNewTab ? 'noopener noreferrer' : undefined"
            @click="trackFooterNavigation(item)"
          >
            {{ item.label }}
          </NuxtLink>
          <a
            v-else-if="isExternalLink(item.href)"
            class="footer-nav-link"
            :href="item.href"
            :target="item.openInNewTab ? '_blank' : undefined"
            :rel="item.openInNewTab ? 'noopener noreferrer' : undefined"
            >{{ item.label }}</a
          >
          <a
            v-else
            class="footer-nav-link"
            :href="item.path ? `/${getCurrentLocale()}${item.path}` : item.href"
            :target="item.openInNewTab ? '_blank' : undefined"
            :rel="item.openInNewTab ? 'noopener noreferrer' : undefined"
            @click.prevent="handleNavClick(item)"
            >{{ item.label }}</a
          >
          <span v-if="index < navItems.length - 1" class="footer-nav-divider" aria-hidden="true">|</span>
        </template>
      </nav>

      <!-- 平台说明与联系方式 -->
      <div class="footer-description">
        <p class="footer-intro">{{ introText }}</p>
        <p v-for="line in contactLines" :key="line" class="footer-contact-line">{{ line }}</p>
        <p class="footer-contact-line" style="margin-top: 20px">※ {{ t('82537') }}</p>
      </div>
    </div>
  </footer>

  <Teleport to="body">
    <section
      v-if="isRiskWarningDialogVisible"
      class="risk-warning-dialog-mask"
      data-testid="footer-risk-warning-dialog"
      @click.self="closeRiskWarningDialog"
    >
      <div class="risk-warning-dialog" role="dialog" aria-modal="true" aria-labelledby="risk-warning-dialog-title">
        <header class="risk-warning-dialog-header">
          <div class="risk-warning-dialog-title-row">
            <h2 id="risk-warning-dialog-title" class="risk-warning-dialog-title">{{ t('82525') }}</h2>
            <button
              type="button"
              class="risk-warning-dialog-close"
              aria-label="关闭风险提示弹框"
              @click="closeRiskWarningDialog"
            >
              <RiskDialogClose aria-hidden="true" />
            </button>
          </div>
        </header>

        <div class="risk-warning-dialog-body">
          <div class="risk-warning-dialog-content">
            <p v-for="paragraph in riskWarningParagraphs" :key="paragraph" class="risk-warning-dialog-text">
              {{ paragraph }}
            </p>
          </div>

          <button type="button" class="risk-warning-dialog-confirm" @click="closeRiskWarningDialog">
            {{ t('82616') }}
          </button>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<style scoped lang="less">
:global(html.risk-warning-dialog-open) {
  /* RTL 页面根节点存在 transform 时，fixed 子元素会退化为相对文档定位。 */
  transform: none !important;
}

.wiki-bit-pc-bootom-map {
  /* 页脚根容器：深色背景，承载正文与上浮面板。 */
  position: relative;
  width: 100%;
  background: #030b20;
  font-family: 'PingFang SC', sans-serif;

  .country-panel {
    /* 国家面板：绝对定位在页脚上方。 */
    position: absolute;
    left: 0;
    bottom: 100%;
    width: 100%;
    padding: 24px 0;
    background: #fff;
    box-shadow:
      0 8px 20px rgb(0 0 0 / 20%),
      0 -4px 12px rgb(0 0 0 / 5%);
    z-index: 30;
  }

  .country-panel__container {
    /* 面板内容区：与页脚正文保持一致的中心宽度。 */
    width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .country-panel-header-row {
    /* 面板头部：当前国家信息和搜索区横向排列。 */
    width: 1200px;
    height: 48px;
    display: flex;
    align-items: center;
    gap: 40px;
  }

  .country-panel-current-country {
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 24px;
      height: 24px;
      object-fit: cover;
      display: block;
    }
  }

  .country-panel-map-icon {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    :deep(svg) {
      width: 15px;
      height: 18.1066px;
      display: block;
    }
  }

  .country-panel-current-name {
    color: #292a2e;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    white-space: nowrap;
  }

  .country-panel-search-box {
    position: relative;
    width: 320px;
    height: 48px;
    border: 1px solid rgb(5 21 36 / 6%);
    border-radius: 1000px;
    background: #f8f8f8;
    padding: 0 12px 0 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .country-panel-search-input-wrap {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    gap: 8px;
  }

  .country-panel-search-input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: #292a2e;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    caret-color: #292a2e;
    padding: 0;

    &::placeholder {
      color: #7d818a;
    }
  }

  .country-panel-search-icons {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .country-panel-close-icon,
  .country-panel-search-icon {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .country-panel-close-icon {
    border: none;
    padding: 0;
    background: transparent;
    cursor: pointer;
  }

  .country-panel-close-icon :deep(svg),
  .country-panel-search-icon :deep(svg) {
    width: 20px;
    height: 20px;
    display: block;
  }

  .country-panel-list-row {
    /* 国家列表区：默认单行多列，断点下改为换行布局。 */
    width: 1200px;
    padding: 16px 0;
    border-top: 1px solid rgb(5 21 36 / 6%);
    display: flex;
    gap: 8px;
    align-items: stretch;
  }

  .country-panel-search-popover {
    /* 搜索建议浮层：定位在搜索输入下方。 */
    position: absolute;
    inset-inline-start: 0;
    top: calc(100% + 10px);
    width: 320px;
    max-height: 260px;
    border-radius: 8px;
    background: #fff;
    padding: 10px 0;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    box-shadow:
      0 8px 24px 0 rgb(0 0 0 / 10%),
      0 16px 48px 0 rgb(0 0 0 / 10%);
    z-index: 35;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .country-panel-search-result-item {
    width: 100%;
    height: 48px;
    border: none;
    background: #fff;
    padding: 0 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background: #f8f8f8;
    }
  }

  .country-panel-search-result-flag {
    width: 24px;
    height: 24px;
    object-fit: cover;
    display: block;
    flex: none;
  }

  .country-panel-search-result-name {
    color: #3d3d3d;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .country-panel-search-result-name--matched {
    color: #ff4d4f;
  }

  .country-panel-column {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-self: stretch;
  }

  .country-panel-column-divider {
    width: 1px;
    position: relative;
    flex: none;
    align-self: stretch;
    display: block;

    &::before {
      content: '';
      position: absolute;
      top: 16px;
      bottom: 16px;
      left: 50%;
      width: 1px;
      background: rgb(5 21 36 / 6%);
      transform: translateX(-50%);
    }

    :deep(svg) {
      display: none;
    }
  }

  .country-panel-item {
    /* 单个国家条目：整行可点击。 */
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 2px;
    background: transparent;
    padding: 0 0 0 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background: #f8f8f8;
    }
  }

  .country-panel-item--selected {
    padding: 0 8px;
    background: #f8f8f8;
  }

  .country-panel-item-flag {
    width: 24px;
    height: 24px;
    object-fit: cover;
    display: block;
    flex: none;
    border: 0.5px solid rgba(5, 21, 36, 0.12);
    border-radius: 50%;
  }

  .country-panel-item-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .country-panel-item-name {
    color: #292a2e;
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .country-panel-item-name--selected {
    font-weight: 600;
  }

  .country-panel-item-url {
    margin-top: 0;
    color: #7d818a;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .country-panel-item-check {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;

    :deep(svg) {
      width: 14.1425px;
      height: 10.0175px;
      display: block;
    }
  }

  .wiki-bit-pc-bootom-map__container {
    /* 页脚正文容器。 */
    width: 1200px;
    margin: 0 auto;
    padding-top: 16px;
    padding-bottom: 40px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .footer-label-row {
    width: 100%;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .footer-brand {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  .footer-brand-logo {
    display: block;
    width: 156px;
    height: 38px;
    flex: none;
  }

  .footer-brand-subtitle {
    margin: 0;
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    // white-space: nowrap;
  }

  .footer-socials {
    width: 187px;
    height: 36px;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: none;
  }

  .footer-social-icon {
    display: block;
    flex: none;
  }

  .footer-social-link {
    color: inherit;
    line-height: 0;
    text-decoration: none;
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;

    &:hover {
      opacity: 0.8;
      transform: translateY(-2px);
    }

    &:focus-visible {
      outline: 2px solid #5b99e2;
      outline-offset: 3px;
    }
  }

  .footer-social-icon--facebook,
  .footer-social-icon--linkedin {
    width: 35px;
    height: 35px;
  }

  .footer-social-icon--telegram {
    width: 34.0541px;
    height: 35px;
  }

  .footer-social-x {
    position: relative;
    width: 34.0541px;
    height: 34.0541px;
  }

  .footer-social-x-background,
  .footer-social-x-mark {
    position: absolute;
    display: block;
  }

  .footer-social-x-background {
    inset: 0;
    width: 34.0541px;
    height: 34.0541px;
  }

  .footer-social-x-mark {
    top: 9px;
    left: 9px;
    width: 16px;
    height: 16px;
  }

  .footer-country {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: none;
  }

  .footer-country-label {
    margin: 0;
    color: #8c8f97;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    white-space: nowrap;
  }

  .footer-country-select-wrap {
    /* 触发器包裹层：用于承接 hover 事件与弹层联动。 */
    position: relative;
    width: fit-content;
    min-width: 164px;
    height: 32px;
    padding-right: 7px;
    flex: none;
  }

  .footer-country-select {
    position: relative;
    width: max-content;
    min-width: 157px;
    height: 32px;
    border: 1px solid #5b99e2;
    border-radius: 2px;
    background: rgb(91 153 226 / 20%);
    padding: 0 8px 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    gap: 6px;
  }

  .footer-country-prefix {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .footer-country-flag {
    width: 18px;
    height: 18px;
    display: block;
    object-fit: cover;
    border-radius: 1px;
    flex: none;
  }

  .footer-country-name {
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    white-space: nowrap;
  }

  .footer-country-arrow {
    width: 12px;
    height: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    transition: transform 0.2s ease;
  }

  .footer-country-arrow--expanded {
    transform: rotate(180deg);
  }

  .footer-country-arrow :deep(svg) {
    width: 8.5px;
    height: 4.5px;
    display: block;
  }

  .footer-country-clear {
    position: absolute;
    right: 0;
    top: 21px;
    width: 14px;
    height: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    padding: 0;
    background: transparent;
    appearance: none;
    cursor: pointer;
  }

  .footer-country-clear :deep(svg) {
    width: 14px;
    height: 14px;
    display: block;
  }

  .footer-divider {
    width: 100%;
    height: 0;
  }

  .footer-divider-line {
    width: 1200px;
    height: 1px;
    display: block;
  }

  .footer-nav {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 16px;
    white-space: nowrap;
    flex-wrap: wrap;
  }

  .footer-nav-link {
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: #5b99e2;
    }
  }

  .footer-nav-button {
    border: 0;
    background: transparent;
    padding: 0;
    font-family: inherit;
  }

  .footer-nav-divider {
    color: rgb(255 255 255 / 24%);
    font-size: 14px;
    font-weight: 300;
    line-height: 20px;
  }

  .footer-description {
    width: 100%;
    color: #7d818a;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
  }

  .footer-intro,
  .footer-contact-line {
    margin: 0;
    width: 100%;
  }

  .footer-contact-html {
    width: 100%;

    :deep(*) {
      color: inherit;
    }

    :deep(p) {
      margin: 0;
      width: 100%;
    }

    :deep(p + p) {
      margin-top: 0;
    }

    :deep(a) {
      color: inherit;
      text-decoration: none;
    }
  }
}

.risk-warning-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 3000;
  width: 100vw;
  height: 100vh;
  background: rgb(9 20 51 / 48%);
}

.risk-warning-dialog {
  /* 弹框直接相对视口定位，避免受页脚或页面内容高度影响。 */
  position: fixed;
  inset: 0;
  width: 540px;
  height: 450px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 64px);
  margin: auto;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  font-family: 'PingFang SC', sans-serif;
}

.risk-warning-dialog-header {
  height: 80px;
  padding: 32px 32px 0;
  background: #fff;
  box-sizing: border-box;
}

.risk-warning-dialog-title-row {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-bottom: 1px solid rgb(5 21 36 / 6%);
  padding-bottom: 16px;
  box-sizing: border-box;
}

.risk-warning-dialog-title {
  width: 436px;
  min-width: 0;
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.risk-warning-dialog-close {
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: 0;
  border-radius: 50%;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  &:hover {
    opacity: 0.82;
    transform: scale(1.03);
  }

  &:focus-visible {
    outline: 2px solid #112d73;
    outline-offset: 2px;
  }

  :deep(svg) {
    width: 32px;
    height: 32px;
    display: block;
  }
}

.risk-warning-dialog-body {
  height: 370px;
  padding: 20px 32px 32px;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.risk-warning-dialog-content {
  width: 476px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 268px;
  overflow: auto;
}

.risk-warning-dialog-text {
  width: 476px;
  margin: 0;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  word-break: break-word;
}

.risk-warning-dialog-confirm {
  width: 184px;
  height: 46px;
  border: 0;
  border-radius: 100px;
  background: #112d73;
  padding: 12px 64px;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: #173b91;
  }

  &:focus-visible {
    outline: 2px solid #5b99e2;
    outline-offset: 2px;
  }
}

/* 弹层进出场动画：轻微位移 + 透明度渐变。 */
.country-panel-drawer-enter-active,
.country-panel-drawer-leave-active {
  transition:
    transform 0.24s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.24s ease;
}

.country-panel-drawer-enter-from,
.country-panel-drawer-leave-to {
  transform: translateY(14px);
  opacity: 0;
}

.country-panel-drawer-enter-to,
.country-panel-drawer-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* 801~1200px：容器改为流式，国家列表可换行。 */
@media screen and (max-width: 1200px) and (min-width: 801px) {
  .wiki-bit-pc-bootom-map {
    .country-panel__container,
    .wiki-bit-pc-bootom-map__container {
      width: calc(100% - 48px);
    }

    .country-panel-header-row,
    .country-panel-list-row {
      width: 100%;
    }

    .country-panel-list-row {
      flex-wrap: wrap;
      align-items: flex-start;
      row-gap: 16px;
    }

    .country-panel-column {
      display: contents;
    }

    .country-panel-item {
      flex: 0 0 188px;
    }

    .country-panel-column-divider {
      display: none;
    }

    .footer-divider-line {
      width: 100%;
    }
  }
}

/* <=800px：按项目既有策略保持 800px 固定布局。 */
@media screen and (max-width: 800px) {
  .wiki-bit-pc-bootom-map {
    width: 800px;
    min-width: 800px;
    max-width: none;

    .country-panel__container,
    .wiki-bit-pc-bootom-map__container {
      width: 800px;
      padding-left: 24px;
      padding-right: 24px;
      box-sizing: border-box;
    }

    .country-panel-header-row,
    .country-panel-list-row {
      width: 100%;
    }

    .country-panel-list-row {
      flex-wrap: wrap;
      align-items: flex-start;
      row-gap: 16px;
    }

    .country-panel-column {
      display: contents;
    }

    .country-panel-item {
      flex: 0 0 188px;
    }

    .country-panel-column-divider {
      display: none;
    }

    .footer-divider-line {
      width: 100%;
    }
  }
}

@media screen and (max-width: 588px) {
  .risk-warning-dialog-mask {
    width: 100vw;
    height: 100vh;
  }

  .risk-warning-dialog {
    width: calc(100vw - 48px);
    height: min-content;
    min-height: 450px;
  }

  .risk-warning-dialog-title,
  .risk-warning-dialog-content,
  .risk-warning-dialog-text {
    width: 100%;
  }

  .risk-warning-dialog-body {
    height: auto;
    min-height: 370px;
  }
}
</style>
